import { useAppDispatch, useIsSelfRegistered } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useLogoutMutation } from '@/store/api/authApi';
import { setRulesModalOpen, toggleAgeModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n';

type UserDropdownProps = {
  isDemoUser: boolean;
  username: string;
  showBalance: boolean;
  showExposure: boolean;
  handleBalanceChange: (value: boolean) => void;
  handleExposureChange: (value: boolean) => void;
};
const UserDropdown: React.FC<UserDropdownProps> = ({
  isDemoUser,
  username,
  handleBalanceChange,
  handleExposureChange,
  showBalance,
  showExposure,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const isSelfRegistered = useIsSelfRegistered();
  const { t } = useTranslation();

  // Deposit and Withdraw aren't here: they're buttons right next to this menu in the header.
  const selectItems = [
    {
      label: t('menu.profile'),
      path: '/profile',
    },
    {
      label: t('menu.accountStatement'),
      path: '/user/account-statement',
    },
    {
      label: t('menu.transactionReport'),
      path: '/user/transactions',
      hideForDemo: true,
    },
    {
      label: t('menu.betHistory'),
      path: '/user/bet-history',
    },
    {
      label: t('menu.activityLogs'),
      path: '/user/activity-logs',
      protected: true,
    },
    {
      label: t('menu.casinoBetHistory'),
      path: '/user/casino-bet-history',
      protected: true,
    },
    {
      label: t('menu.bonusStatement'),
      path: '/user/bonus-statement',
      hideForDemo: true,
    },
    {
      label: t('menu.referral'),
      path: '/referral',
      hideForDemo: true,
      hideForNonSelfRegistered: true,
    },
  ];

  const itemClass =
    'px-3 py-1.5 text-sm text-shell-fg/90 rounded-md cursor-pointer outline-none data-[highlighted]:bg-shell-raised data-[highlighted]:text-shell-fg';

  const handleLogout = async () => {
    try {
      const res = await logout().unwrap();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-2 rounded-md border border-shell-border bg-shell-raised px-1.5 text-sm sm:px-2 text-shell-fg transition hover:border-gold focus:outline-none data-[state=open]:border-gold"
        >
          <span className="flex size-6 items-center justify-center rounded-full bg-gold text-gold-foreground">
            <Icons.User className="text-sm" />
          </span>
          <span className="hidden max-w-[110px] truncate sm:inline">
            {isDemoUser ? 'demo123' : username}
          </span>
          <Icons.Down className="text-[10px] text-shell-muted max-sm:hidden" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={5}
          className="z-50 min-w-[220px] rounded-lg border border-shell-border bg-shell-panel p-1 shadow-dropdown"
        >
          {/* Dynamic navigation items */}
          {selectItems.map((item) => {
            if (item.hideForDemo && isDemoUser) return null;
            if (item.hideForNonSelfRegistered && !isSelfRegistered) return null;
            return (
              <DropdownMenu.Item
                key={item.path}
                className={itemClass}
                onSelect={() => navigate(item.path)}
              >
                {item.label}
              </DropdownMenu.Item>
            );
          })}

          {/* Redux actions */}
          <DropdownMenu.Item className={itemClass} onSelect={() => dispatch(toggleEditStake())}>
            {t('menu.setButtonValues')}
          </DropdownMenu.Item>

          {!isDemoUser && (
            <>
              {' '}
              {/* <DropdownMenu.Item
              className={itemClass}
              onSelect={() => navigate('/user/secure-auth-verification')}
            >
              Security Auth Verification
            </DropdownMenu.Item> */}
              <DropdownMenu.Item
                className={itemClass}
                onSelect={() => navigate('/user/change-password')}
              >
                {t('menu.changePassword')}
              </DropdownMenu.Item>
            </>
          )}
          <DropdownMenu.Item
            className={`${itemClass} xl:hidden`}
            onSelect={() => dispatch(setRulesModalOpen(true))}
          >
            {t('common.rules')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={`${itemClass} xl:hidden`}
            onSelect={(e) => {
              e.preventDefault();
              handleBalanceChange(!showBalance);
            }}
          >
            <div className="flex items-center justify-between">
              {t('menu.balance')} <input type="checkbox" checked={showBalance} readOnly />
            </div>
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={`${itemClass} xl:hidden`}
            onSelect={(e) => {
              e.preventDefault();
              handleExposureChange(!showExposure);
            }}
          >
            <div className="flex items-center justify-between">
              {t('menu.exposure')} <input checked={showExposure} readOnly type="checkbox" />
            </div>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-shell-border" />
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold text-danger-red outline-none data-[highlighted]:bg-danger-red/10"
            onSelect={handleLogout}
          >
            <Icons.logout className="size-4" aria-hidden="true" />
            {t('menu.signOut')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default UserDropdown;
